import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { applyFill } from '../fill';
import { badgeSequence } from '../kits/calloutKit';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { FillValue, ToolRecipe } from '../types';

/**
 * Step numbers — a run of auto-numbered circle badges a teacher drops beside
 * a list of instructions (CATALOG, calloutKit.badgeSequence). THE
 * AUTO-SEQUENCE LAW: the labels are DERIVED from count + start
 * (start..start+count-1), never hand-authored, so bumping the count from 3 to
 * 6 renumbers ①..⑥ with no stragglers. The numbers are therefore NOT
 * param-tagged — there is nothing to double-click; the count/start fields are
 * the single source of truth.
 *
 * Emits Circle + FabricText only, grouped and tagged {tool,version,params} —
 * plain fabric JSON, vector at any export dpi.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern).
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

const DEFAULT_FONT = 'Arial';
const DEFAULT_FILL = '#2563eb';
const DEFAULT_NUMBER = '#ffffff';

const stepBadges: ToolRecipe = {
  id: 'step-badges',
  domain: 'display',
  tags: ['steps', 'sequence', 'numbers', 'directions', 'instructions', 'how-to'],
  name: 'Step numbers',
  category: 'callouts',
  version: 1,
  description:
    'A row (or column) of auto-numbered circle badges for step-by-step instructions. Change "How many" and they renumber themselves.',
  defaults: {
    count: 3,
    start: 1,
    direction: 'row',
    size: 72,
    color: DEFAULT_FILL,
    numberColor: DEFAULT_NUMBER,
  },
  schema: [
    { key: 'count', label: 'How many', type: 'number', min: 1, max: 10, step: 1 },
    {
      key: 'start',
      label: 'Start at',
      type: 'number',
      min: 0,
      max: 999,
      step: 1,
      help: 'The first number in the run.',
    },
    {
      key: 'direction',
      label: 'Direction',
      type: 'select',
      options: [
        { value: 'row', label: 'Across (row)' },
        { value: 'column', label: 'Down (column)' },
      ],
    },
    { key: 'size', label: 'Badge size', type: 'number', min: 16, max: 300, step: 2, section: 'Style' },
    { key: 'color', label: 'Circle color', type: 'fill', section: 'Style', help: 'Solid, gradient, or texture.' },
    { key: 'numberColor', label: 'Number color', type: 'fill', section: 'Style', help: 'Solid, gradient, or texture.' },
  ],
  async generate(params) {
    const count = clamp(Math.round(num(params.count, 3)), 1, 10);
    const start = Math.round(num(params.start, 1));
    const direction: 'row' | 'column' = params.direction === 'column' ? 'column' : 'row';
    const size = clamp(num(params.size, 72), 12, 400);
    // Fills upgraded to FillValue: a solid string keeps the str() coercion
    // (back-compat), an object becomes a gradient/texture. Both land on fabric
    // `fill` — the badge circle and the number text. The pure kit types
    // fill/textColor as strings, so pass placeholder solids through it and
    // apply the real fills to the assembled objects after.
    const circleFill: FillValue =
      typeof params.color === 'string' || params.color == null
        ? str(params.color, DEFAULT_FILL)
        : (params.color as FillValue);
    const numberFill: FillValue =
      typeof params.numberColor === 'string' || params.numberColor == null
        ? str(params.numberColor, DEFAULT_NUMBER)
        : (params.numberColor as FillValue);

    const block = badgeSequence({
      count,
      start,
      r: size / 2,
      direction,
      fill: DEFAULT_FILL,
      textColor: DEFAULT_NUMBER,
      fontFamily: DEFAULT_FONT,
    });

    // badgeSequence emits [circle, label, circle, label, …] — every circle
    // wears the badge fill, every label the number fill. Resolve once per
    // object by spec kind (a solid string resolves to itself → byte-identical).
    const objs = assembleSpecs(block.specs);
    await Promise.all(
      objs.map((o, i) => {
        const spec = block.specs[i];
        if (spec.kind === 'circle') return applyFill(o, circleFill, DEFAULT_FILL);
        if (spec.kind === 'text') return applyFill(o, numberFill, DEFAULT_NUMBER);
        return Promise.resolve();
      }),
    );

    const group = new Group(objs);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, stepBadges, params);
  },
};

registerRecipe(stepBadges);
